/**
 * RadarDolor.js v4.4.31 — Radar de empresas perdedoras seriales.
 *
 * Vista de agregación sobre oportunidades_placsp + archivadas:
 *   - Ranking de empresas que más han PERDIDO licitaciones PLACSP
 *   - Filtros: sector, min. derrotas, fecha desde, orden
 *   - Drilldown a detalle completo por empresa (todas sus derrotas, rivales,
 *     sectores donde pierde, órganos habituales)
 *   - Botón rápido "Convertir a Lead" desde el drilldown
 *
 * Endpoints consumidos:
 *   GET  /api/empresas-licitadoras/ranking
 *   GET  /api/empresas-licitadoras/{nombre}/detalle
 */
import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/services/api";
import {
  Crosshair,
  TrendingDown,
  Building2,
  Euro,
  Users,
  RefreshCw,
  FilterX,
  ChevronRight,
  Search,
  Loader2,
} from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import { formatEUR } from "@/lib/empresa-licitadora";


const PERIODOS = [
  { value: "all", label: "Todo el histórico" },
  { value: "365d", label: "Último año" },
  { value: "180d", label: "Últimos 6 meses" },
  { value: "90d", label: "Últimos 3 meses" },
];

const fechaDesde = (periodo) => {
  if (periodo === "all") return null;
  const dias = parseInt(periodo.replace("d", ""));
  const d = new Date();
  d.setDate(d.getDate() - dias);
  return d.toISOString().slice(0, 10);
};

export default function RadarDolor() {
  const navigate = useNavigate();

  // Filtros
  const [sector, setSector] = useState("todos");
  const [minDerrotas, setMinDerrotas] = useState(2);
  const [periodo, setPeriodo] = useState("all");
  const [orden, setOrden] = useState("derrotas");
  const [search, setSearch] = useState("");

  // Data
  const [ranking, setRanking] = useState([]);
  const [loading, setLoading] = useState(false);
  const [tiposSrs, setTiposSrs] = useState([]);

  // Fetch tipos SRS disponibles
  useEffect(() => {
    api.get("/oportunidades/tipos-srs")
      .then((resp) => setTiposSrs(resp.data || []))
      .catch(() => setTiposSrs([]));
  }, []);

  // Fetch ranking
  const loadRanking = useCallback(async () => {
    setLoading(true);
    try {
      const params = {
        min_derrotas: minDerrotas,
        limit: 100,
        orden,
        incluir_archivadas: true,
      };
      if (sector && sector !== "todos") params.sector = sector;
      const desde = fechaDesde(periodo);
      if (desde) params.desde = desde;

      const resp = await api.get("/empresas-licitadoras/ranking", { params });
      setRanking(resp.data.ranking || []);
    } catch (error) {
      console.error("Error loading ranking:", error);
      toast.error("Error al cargar el ranking");
    } finally {
      setLoading(false);
    }
  }, [sector, minDerrotas, periodo, orden]);

  useEffect(() => {
    loadRanking();
  }, [loadRanking]);

  // Click empresa → navegar a la página dedicada (no modal)
  const abrirDetalle = (empresa) => {
    navigate(`/radar-dolor/empresa/${encodeURIComponent(empresa)}`);
  };

  // Filtrar por search (cliente)
  const filteredRanking = ranking.filter((r) =>
    !search || (r.empresa || "").toLowerCase().includes(search.toLowerCase())
  );

  const clearFiltros = () => {
    setSector("todos");
    setMinDerrotas(2);
    setPeriodo("all");
    setOrden("derrotas");
    setSearch("");
  };

  const hasFiltros =
    (sector && sector !== "todos") ||
    minDerrotas !== 2 ||
    periodo !== "all" ||
    orden !== "derrotas" ||
    search;

  return (
    <div className="p-6 min-h-screen">
      {/* Header */}
      <div className="mb-6">
        <div className="flex items-center gap-3 mb-2">
          <div className="p-2 rounded-lg bg-red-500/15 border border-red-500/40">
            <Crosshair className="w-6 h-6 text-red-600 dark:text-red-400" />
          </div>
          <div>
            <h1 className="text-2xl font-bold theme-text">Radar de Dolor</h1>
            <p className="theme-text-muted text-sm">
              Empresas detectadas perdiendo licitaciones PLACSP. Abre el detalle para ver perfil comercial: <span className="text-red-700 dark:text-red-400 font-medium">serial loser</span>, <span className="text-orange-700 dark:text-orange-400 font-medium">en riesgo</span>, o <span className="text-emerald-700 dark:text-emerald-400 font-medium">high performer con ventana</span>.
            </p>
          </div>
        </div>
      </div>

      {/* Filtros */}
      <Card className="mb-4 theme-card">
        <CardContent className="p-4">
          <div className="flex flex-wrap gap-3 items-end">
            <div className="flex-1 min-w-[200px]">
              <label className="text-xs theme-text-muted mb-1 block">Buscar empresa</label>
              <div className="relative">
                <Search className="absolute left-2.5 top-2.5 w-4 h-4 theme-text-muted" />
                <Input
                  placeholder="Filtrar por nombre..."
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  className="pl-8"
                />
              </div>
            </div>

            <div className="min-w-[180px]">
              <label className="text-xs theme-text-muted mb-1 block">Sector</label>
              <Select value={sector} onValueChange={setSector}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="todos">Todos los sectores</SelectItem>
                  {tiposSrs.map((t) => (
                    <SelectItem key={t} value={t}>{t}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="min-w-[140px]">
              <label className="text-xs theme-text-muted mb-1 block">Min. derrotas</label>
              <Select
                value={String(minDerrotas)}
                onValueChange={(v) => setMinDerrotas(Number(v))}
              >
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  {[1, 2, 3, 5, 10].map((n) => (
                    <SelectItem key={n} value={String(n)}>{n}+</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="min-w-[160px]">
              <label className="text-xs theme-text-muted mb-1 block">Periodo</label>
              <Select value={periodo} onValueChange={setPeriodo}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  {PERIODOS.map((p) => (
                    <SelectItem key={p.value} value={p.value}>{p.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="min-w-[180px]">
              <label className="text-xs theme-text-muted mb-1 block">Ordenar por</label>
              <Select value={orden} onValueChange={setOrden}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="derrotas">Nº de derrotas</SelectItem>
                  <SelectItem value="importe_perdido">Importe perdido</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <Button
              variant="outline"
              size="icon"
              title="Recargar"
              onClick={loadRanking}
              disabled={loading}
            >
              <RefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} />
            </Button>

            <Button
              variant="outline"
              onClick={clearFiltros}
              disabled={!hasFiltros}
              className="whitespace-nowrap"
            >
              <FilterX className="w-4 h-4 mr-1.5" />
              Limpiar
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Summary KPI row */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-3 mb-4">
        <Card className="theme-card">
          <CardContent className="p-4">
            <div className="flex items-center gap-2 theme-text-muted text-xs uppercase mb-1">
              <Users className="w-3.5 h-3.5" /> Empresas detectadas
            </div>
            <div className="text-2xl font-bold theme-text">{filteredRanking.length}</div>
          </CardContent>
        </Card>
        <Card className="theme-card">
          <CardContent className="p-4">
            <div className="flex items-center gap-2 theme-text-muted text-xs uppercase mb-1">
              <TrendingDown className="w-3.5 h-3.5" /> Total derrotas
            </div>
            <div className="text-2xl font-bold theme-text">
              {filteredRanking.reduce((s, r) => s + (r.derrotas || 0), 0)}
            </div>
          </CardContent>
        </Card>
        <Card className="theme-card">
          <CardContent className="p-4">
            <div className="flex items-center gap-2 theme-text-muted text-xs uppercase mb-1">
              <Euro className="w-3.5 h-3.5" /> Importe licitado (en derrotas)
            </div>
            <div className="text-2xl font-bold theme-text">
              {formatEUR(filteredRanking.reduce((s, r) => s + (r.importe_total_licitado || 0), 0))}
            </div>
          </CardContent>
        </Card>
        <Card className="theme-card">
          <CardContent className="p-4">
            <div className="flex items-center gap-2 theme-text-muted text-xs uppercase mb-1">
              <Crosshair className="w-3.5 h-3.5" /> Top perdedor
            </div>
            <div className="text-lg font-bold theme-text truncate">
              {filteredRanking[0]?.empresa || "—"}
            </div>
            <div className="text-xs theme-text-muted">
              {filteredRanking[0]?.derrotas || 0} derrotas
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Tabla ranking */}
      <Card className="theme-card">
        <CardContent className="p-0">
          {loading ? (
            <div className="p-12 text-center theme-text-muted">
              <Loader2 className="w-8 h-8 animate-spin mx-auto mb-2" />
              Cargando ranking...
            </div>
          ) : filteredRanking.length === 0 ? (
            <div className="p-12 text-center theme-text-muted">
              <Crosshair className="w-8 h-8 mx-auto mb-2 opacity-50" />
              No se encontraron empresas con los filtros actuales.
              {minDerrotas > 1 && (
                <div className="text-xs mt-2">
                  Prueba bajar "Min. derrotas" a 1 o ampliar el periodo.
                </div>
              )}
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b theme-border text-xs uppercase theme-text-muted">
                    <th className="text-left py-3 px-4 w-10">#</th>
                    <th className="text-left py-3 px-4">Empresa</th>
                    <th className="text-right py-3 px-4">Derrotas</th>
                    <th className="text-right py-3 px-4 hidden md:table-cell">Importe licitado</th>
                    <th className="text-left py-3 px-4 hidden lg:table-cell">Sectores</th>
                    <th className="text-left py-3 px-4 hidden lg:table-cell">Principales rivales</th>
                    <th className="text-right py-3 px-4">Acción</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredRanking.map((r, idx) => (
                    <tr
                      key={r.empresa}
                      className="border-b theme-border hover:theme-bg-tertiary cursor-pointer transition"
                      onClick={() => abrirDetalle(r.empresa)}
                    >
                      <td className="py-3 px-4 theme-text-muted text-sm font-mono">{idx + 1}</td>
                      <td className="py-3 px-4">
                        <div className="flex items-center gap-2">
                          <Building2 className="w-4 h-4 theme-text-muted flex-shrink-0" />
                          <div className="min-w-0">
                            <div className="theme-text font-medium truncate max-w-md">{r.empresa}</div>
                            {r.nif && (
                              <div className="text-xs theme-text-muted font-mono">{r.nif}</div>
                            )}
                          </div>
                        </div>
                      </td>
                      <td className="py-3 px-4 text-right">
                        <Badge
                          className={`font-mono border ${
                            r.derrotas >= 5
                              ? "bg-red-500/15 text-red-700 dark:text-red-300 border-red-500/40"
                              : r.derrotas >= 3
                              ? "bg-orange-500/15 text-orange-700 dark:text-orange-300 border-orange-500/40"
                              : "bg-yellow-500/20 text-yellow-800 dark:text-yellow-300 border-yellow-500/40"
                          }`}
                        >
                          {r.derrotas}x
                        </Badge>
                      </td>
                      <td className="py-3 px-4 text-right hidden md:table-cell">
                        <span className="theme-text font-mono text-sm">
                          {formatEUR(r.importe_total_licitado)}
                        </span>
                      </td>
                      <td className="py-3 px-4 hidden lg:table-cell">
                        <div className="flex flex-wrap gap-1">
                          {(r.sectores || []).slice(0, 2).map((s) => (
                            <Badge key={s} variant="outline" className="text-xs">{s}</Badge>
                          ))}
                          {(r.sectores || []).length > 2 && (
                            <span className="text-xs theme-text-muted">+{r.sectores.length - 2}</span>
                          )}
                        </div>
                      </td>
                      <td className="py-3 px-4 hidden lg:table-cell">
                        <div className="text-xs theme-text-muted truncate max-w-xs">
                          {(r.ganadores_que_la_vencieron || []).slice(0, 2).join(", ") || "—"}
                          {(r.ganadores_que_la_vencieron || []).length > 2 && (
                            <span className="ml-1">+{r.ganadores_que_la_vencieron.length - 2}</span>
                          )}
                        </div>
                      </td>
                      <td className="py-3 px-4 text-right">
                        <Button size="sm" variant="ghost" className="theme-text-muted">
                          Ver detalle <ChevronRight className="w-4 h-4 ml-1" />
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

    </div>
  );
}
